import { useCallback, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Loader2, Maximize2, Mic, Minimize2, Send, Square, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { fetchWithAuth } from '@/lib/auth'
import { usePermissions } from '@/hooks/usePermissions'
import { useChatStore, type ChatMessage } from '@/store/chatStore'
import { useAudioRecorder } from '@/hooks/useAudioRecorder'
import { cn } from '@/lib/utils'

// How many messages of history to send with each turn
const HISTORY_WINDOW = 20
// Abort slow requests (agentic turns with several tool calls can take a while)
const REQUEST_TIMEOUT_MS = 120_000

const MIN_WIDTH = 340
const MIN_HEIGHT = 400
const DEFAULT_WIDTH = 420
const DEFAULT_HEIGHT = 600
const STORAGE_KEY = 'chatWidgetSize'

function loadSize(): { width: number; height: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        width: Math.max(MIN_WIDTH, parsed.width || DEFAULT_WIDTH),
        height: Math.max(MIN_HEIGHT, parsed.height || DEFAULT_HEIGHT),
      }
    }
  } catch { /* ignore */ }
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT }
}

function saveSize(width: number, height: number) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ width, height }))
}

// One-time greeting bubble shown next to the launcher on the first visit
const GREETING_KEY = 'chatWidgetGreeted'
// Posición del botón flotante elegida por el usuario (offsets right/bottom)
const LAUNCHER_POS_KEY = 'chatLauncherPos'

// Example prompts shown as tappable chips when the conversation is empty
const SUGGESTIONS = [
  '¿Cuántos socios activos hay?',
  'Creá una plantilla de reporte de morosos',
  'Listá los socios morosos',
]

/**
 * Animated assistant character: a sober support-agent face with a headset.
 * Eyes blink and the head tilts occasionally (disabled under reduced motion).
 */
function AssistantAvatar({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <circle cx="24" cy="24" r="22" fill="#ffffff" />
      <g className="chat-avatar-head">
        <circle cx="24" cy="25" r="14" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="1" />
        <rect className="chat-avatar-eye" x="17.5" y="21" width="4" height="6.5" rx="2" fill="#1e3a8a" />
        <rect className="chat-avatar-eye" x="26.5" y="21" width="4" height="6.5" rx="2" fill="#1e3a8a" />
        <path
          d="M19.5 31.5c1.4 1.3 3 2 4.5 2s3.1-.7 4.5-2"
          stroke="#1e3a8a"
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M10.5 24a13.5 13.5 0 0 1 27 0"
          stroke="#4f46e5"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        />
        <rect x="8.2" y="21.5" width="4.6" height="8" rx="2.3" fill="#4f46e5" />
        <rect x="35.2" y="21.5" width="4.6" height="8" rx="2.3" fill="#4f46e5" />
        <path
          d="M37.5 29.5c0 4.5-4 7.5-8.5 8"
          stroke="#4f46e5"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
        <circle cx="28.5" cy="37.6" r="1.9" fill="#4f46e5" />
      </g>
    </svg>
  )
}

/** Markdown renderer with polished styles for chat bubbles */
function AssistantMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
        ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
        li: ({ children }) => <li className="leading-relaxed">{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-foreground dark:text-white">{children}</strong>,
        em: ({ children }) => <em className="text-muted-foreground dark:text-muted-foreground">{children}</em>,
        h2: ({ children }) => <h2 className="text-sm font-bold mt-3 mb-1 text-foreground dark:text-white">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1 text-foreground dark:text-foreground">{children}</h3>,
        hr: () => <hr className="my-2 border-border dark:border-border" />,
        a: ({ children, href }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
            {children}
          </a>
        ),
        code: ({ children }) => (
          <code className="bg-muted dark:bg-muted rounded px-1 py-0.5 text-xs font-mono">{children}</code>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-2 rounded-lg border border-border dark:border-border">
            <table className="text-xs border-collapse w-full">{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-blue-50 dark:bg-blue-950">{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody className="[&>tr:nth-child(even)]:bg-muted dark:[&>tr:nth-child(even)]:bg-accent/50">{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr className="border-b border-border dark:border-border last:border-b-0">{children}</tr>
        ),
        th: ({ children }) => (
          <th className="px-3 py-1.5 text-left text-xs font-semibold text-blue-800 dark:text-blue-300 whitespace-nowrap">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-3 py-1.5 text-foreground dark:text-muted-foreground whitespace-nowrap">{children}</td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm break-words',
          isUser
            ? 'bg-blue-600 text-white rounded-br-sm'
            : 'bg-muted dark:bg-card text-foreground dark:text-foreground rounded-bl-sm'
        )}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{message.content}</span>
        ) : (
          <AssistantMarkdown content={message.content} />
        )}
      </div>
    </div>
  )
}

function useResize(
  isOpen: boolean,
  isExpanded: boolean,
) {
  const [size, setSize] = useState(loadSize)
  const dragging = useRef(false)
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 })

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (isExpanded) return
    e.preventDefault()
    e.stopPropagation()
    dragging.current = true
    startPos.current = { x: e.clientX, y: e.clientY, w: size.width, h: size.height }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [size, isExpanded])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    const dx = startPos.current.x - e.clientX
    const dy = startPos.current.y - e.clientY
    const maxW = window.innerWidth - 32
    const maxH = window.innerHeight - 32
    setSize({
      width: Math.min(maxW, Math.max(MIN_WIDTH, startPos.current.w + dx)),
      height: Math.min(maxH, Math.max(MIN_HEIGHT, startPos.current.h + dy)),
    })
  }, [])

  const onPointerUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    setSize(prev => {
      saveSize(prev.width, prev.height)
      return prev
    })
  }, [])

  return { size, onPointerDown, onPointerMove, onPointerUp }
}

export function SociosChatWidget() {
  const { can } = usePermissions()
  const { messages, isOpen, isLoading, error, setOpen, addMessage, setLoading, setError, clearChat } =
    useChatStore()
  const recorder = useAudioRecorder()

  const [input, setInput] = useState('')
  const [transcribing, setTranscribing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [showGreeting, setShowGreeting] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { size, onPointerDown, onPointerMove, onPointerUp } = useResize(isOpen, expanded)

  // Launcher arrastrable: el usuario puede correr el botón si tapa contenido;
  // la posición (offsets right/bottom) se recuerda en localStorage.
  // null = esquina por defecto (bottom-4 right-4).
  const [launcherPos, setLauncherPos] = useState<{ right: number; bottom: number } | null>(() => {
    try {
      return JSON.parse(localStorage.getItem(LAUNCHER_POS_KEY) || 'null')
    } catch {
      return null
    }
  })
  const launcherRef = useRef<HTMLDivElement>(null)
  const suppressClickRef = useRef(false)
  const dragState = useRef<{
    startX: number
    startY: number
    right: number
    bottom: number
    width: number
    height: number
    moved: boolean
  } | null>(null)

  const clampLauncherPos = (pos: { right: number; bottom: number }, width: number, height: number) => ({
    right: Math.min(Math.max(pos.right, 8), Math.max(8, window.innerWidth - width - 8)),
    bottom: Math.min(Math.max(pos.bottom, 8), Math.max(8, window.innerHeight - height - 8)),
  })

  const onLauncherPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const rect = launcherRef.current?.getBoundingClientRect()
    if (!rect) return
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      right: window.innerWidth - rect.right,
      bottom: window.innerHeight - rect.bottom,
      width: rect.width,
      height: rect.height,
      moved: false,
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onLauncherPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragState.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    // umbral de 6px para distinguir click de arrastre
    if (!drag.moved && Math.hypot(dx, dy) < 6) return
    drag.moved = true
    setLauncherPos(
      clampLauncherPos({ right: drag.right - dx, bottom: drag.bottom - dy }, drag.width, drag.height)
    )
  }

  const onLauncherPointerUp = () => {
    const drag = dragState.current
    dragState.current = null
    if (drag?.moved) {
      suppressClickRef.current = true
      setLauncherPos(prev => {
        if (prev) localStorage.setItem(LAUNCHER_POS_KEY, JSON.stringify(prev))
        return prev
      })
    }
  }

  const handleLauncherClick = () => {
    // el click que cierra un arrastre no debe abrir el chat
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    openChat()
  }

  // Si cambia el tamaño de la ventana, mantener el botón dentro del viewport
  useEffect(() => {
    const onWindowResize = () => {
      const rect = launcherRef.current?.getBoundingClientRect()
      if (!rect) return
      setLauncherPos(prev => (prev ? clampLauncherPos(prev, rect.width, rect.height) : prev))
    }
    window.addEventListener('resize', onWindowResize)
    return () => window.removeEventListener('resize', onWindowResize)
  }, [])

  // Auto-scroll to the newest message
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [messages, isLoading, isOpen])

  // First-visit greeting bubble next to the launcher (shown once per browser)
  useEffect(() => {
    if (isOpen || localStorage.getItem(GREETING_KEY)) return
    const timer = setTimeout(() => setShowGreeting(true), 1200)
    return () => clearTimeout(timer)
  }, [isOpen])

  const dismissGreeting = () => {
    setShowGreeting(false)
    localStorage.setItem(GREETING_KEY, '1')
  }

  const openChat = () => {
    dismissGreeting()
    setOpen(true)
  }

  // Solo para usuarios con acceso a reportes/chatbot (recurso 'reports',
  // migración 042) — consultar socios no habilita el asistente
  if (!can('reports', 'read')) {
    return null
  }

  const sendMessage = async () => {
    const content = input.trim()
    if (!content || isLoading) return

    setInput('')
    setError(null)
    addMessage({ role: 'user', content })
    setLoading(true)

    // Send a bounded window of history (backend validates <= 30 messages)
    const history = [...useChatStore.getState().messages]
      .slice(-HISTORY_WINDOW)
      .map(({ role, content }) => ({ role, content }))

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

    try {
      const response = await fetchWithAuth('/api/socios/chat', {
        method: 'POST',
        body: JSON.stringify({ messages: history }),
        signal: controller.signal,
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        setError(data.message || `Error del asistente (${response.status})`)
        return
      }

      addMessage({ role: 'assistant', content: data.data?.text || 'Sin respuesta' })
      window.dispatchEvent(new CustomEvent('report-bank-refresh'))
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setError('La consulta tardó demasiado y fue cancelada. Probá con una pregunta más específica.')
      } else {
        setError('Error de conexión con el asistente.')
      }
    } finally {
      clearTimeout(timeout)
      setLoading(false)
    }
  }

  const handleMicClick = async () => {
    if (recorder.status === 'recording') {
      const blob = await recorder.stop()
      if (!blob) return

      setTranscribing(true)
      setError(null)
      try {
        const form = new FormData()
        const ext = blob.type.includes('mp4') ? 'm4a' : 'webm'
        form.append('audio', blob, `nota-de-voz.${ext}`)

        const response = await fetchWithAuth('/api/socios/chat/transcribe', {
          method: 'POST',
          body: form,
        })
        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError(data.message || 'No se pudo transcribir el audio')
          return
        }

        const text = data.data?.text || ''
        if (text) {
          // Insert into the input so the user can review/edit before sending
          setInput((prev) => (prev ? `${prev} ${text}` : text))
          textareaRef.current?.focus()
        } else {
          setError('No se detectó voz en la grabación.')
        }
      } catch {
        setError('Error de conexión al transcribir el audio.')
      } finally {
        setTranscribing(false)
      }
    } else {
      await recorder.start()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatElapsed = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  return (
    <>
      {/* Floating launcher (arrastrable; posición recordada en localStorage) */}
      {!isOpen && (
        <div
          ref={launcherRef}
          className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2.5"
          style={launcherPos ? { right: launcherPos.right, bottom: launcherPos.bottom } : undefined}
        >
          {showGreeting && (
            <div className="chat-greeting-bubble relative max-w-[250px] rounded-xl rounded-br-sm border border-blue-100 dark:border-border bg-background dark:bg-card shadow-xl px-4 py-3 pr-8">
              <button
                onClick={dismissGreeting}
                className="absolute top-1.5 right-1.5 rounded-full p-1 text-muted-foreground hover:text-muted-foreground dark:hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Cerrar mensaje de bienvenida"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <p className="text-sm font-medium text-foreground dark:text-foreground">
                Hola, soy el asistente de socios.
              </p>
              <p className="mt-1 text-xs text-muted-foreground dark:text-muted-foreground">
                Puedo responder consultas sobre socios y crear plantillas para el Banco de Reportes.
              </p>
            </div>
          )}
          <button
            onClick={handleLauncherClick}
            onPointerDown={onLauncherPointerDown}
            onPointerMove={onLauncherPointerMove}
            onPointerUp={onLauncherPointerUp}
            className="group flex touch-none cursor-grab items-center gap-0 sm:gap-2.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 p-1.5 sm:py-1.5 sm:pl-1.5 sm:pr-5 shadow-lg shadow-blue-600/30 transition-transform hover:scale-[1.04] active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            aria-label="Abrir asistente de socios (mantener presionado y arrastrar para mover)"
            title="Click para abrir · arrastrar para mover"
          >
            <span className="relative h-11 w-11 shrink-0">
              {showGreeting && (
                <span className="chat-launcher-ring absolute inset-0 rounded-full bg-blue-400" />
              )}
              <AssistantAvatar className="relative h-11 w-11 drop-shadow" />
            </span>
            <span className="hidden text-left leading-tight text-white sm:block">
              <span className="block text-sm font-semibold">Asistente IA</span>
              <span className="block text-[11px] text-blue-100">Consultas de socios</span>
            </span>
          </button>
        </div>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className={cn(
            'fixed z-50 flex flex-col bg-background dark:bg-background border border-border dark:border-border shadow-2xl',
            expanded
              ? 'inset-2 rounded-xl'
              : 'inset-0 rounded-none sm:inset-auto sm:bottom-4 sm:right-4 sm:rounded-xl'
          )}
          style={
            expanded
              ? undefined
              : {
                  width: window.innerWidth < 640 ? undefined : `${size.width}px`,
                  height: window.innerWidth < 640 ? undefined : `${size.height}px`,
                  maxHeight: '100dvh',
                }
          }
        >
          {/* Resize handle — top-left corner (widget is anchored bottom-right) */}
          {!expanded && (
            <div
              className="hidden sm:block absolute -top-1 -left-1 w-5 h-5 cursor-nwse-resize z-10 group"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              title="Arrastrar para redimensionar"
            >
              <svg
                viewBox="0 0 16 16"
                className="w-3.5 h-3.5 absolute top-0.5 left-0.5 text-muted-foreground group-hover:text-blue-500 transition-colors rotate-90"
                fill="currentColor"
              >
                <circle cx="3" cy="3" r="1.5" />
                <circle cx="8" cy="3" r="1.5" />
                <circle cx="3" cy="8" r="1.5" />
                <circle cx="13" cy="3" r="1.5" />
                <circle cx="8" cy="8" r="1.5" />
                <circle cx="3" cy="13" r="1.5" />
              </svg>
            </div>
          )}

          {/* Header */}
          <div
            className={cn(
              'flex items-center justify-between px-3 py-2.5 border-b border-border dark:border-border bg-gradient-to-r from-blue-600 to-indigo-600',
              expanded ? 'rounded-t-xl' : 'rounded-t-none sm:rounded-t-xl'
            )}
          >
            <div className="flex items-center gap-2.5 text-white">
              <AssistantAvatar className="h-9 w-9 shrink-0" />
              <div className="leading-tight">
                <span className="block font-semibold text-sm">Asistente de Socios</span>
                <span className="block text-[11px] text-blue-100">
                  {isLoading ? 'Consultando…' : 'En línea'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-blue-700"
                onClick={clearChat}
                title="Limpiar conversación"
                disabled={messages.length === 0}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="hidden sm:inline-flex h-8 w-8 text-white hover:bg-blue-700"
                onClick={() => setExpanded(!expanded)}
                title={expanded ? 'Restaurar tamaño' : 'Expandir'}
              >
                {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-blue-700"
                onClick={() => { setOpen(false); setExpanded(false) }}
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground mt-6 px-4 space-y-3">
                <AssistantAvatar className="h-16 w-16 mx-auto" />
                <p className="font-medium text-foreground dark:text-muted-foreground">
                  ¡Hola! Soy el asistente del módulo de socios.
                </p>
                <p>
                  Puedo buscar socios, listar morosos, responder consultas generales y crear{' '}
                  <strong className="font-medium text-muted-foreground dark:text-muted-foreground">plantillas de reportes</strong>{' '}
                  que después ejecutás desde el Banco de Reportes. Podés escribir o dictar tu pregunta con el micrófono.
                </p>
                <div className="flex flex-wrap justify-center gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setInput(s)
                        textareaRef.current?.focus()
                      }}
                      className="rounded-full border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950 px-3 py-1.5 text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Consultando...
              </div>
            )}
            {(error || recorder.error) && (
              <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {error || recorder.error}
              </div>
            )}
          </div>

          {/* Input */}
          <div
            className="border-t border-border dark:border-border p-2"
            style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
          >
            {recorder.status === 'recording' && (
              <div className="flex items-center gap-2 px-2 pb-2 text-sm text-red-600">
                <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" />
                Grabando... {formatElapsed(recorder.elapsedSeconds)}
                <button className="ml-auto text-muted-foreground underline" onClick={recorder.cancel}>
                  cancelar
                </button>
              </div>
            )}
            <div className="flex items-end gap-1.5">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribí tu consulta..."
                rows={2}
                className="flex-1 resize-none text-base sm:text-sm min-h-[40px] max-h-32 rounded-md border border-border bg-background dark:bg-card dark:border-border dark:text-foreground px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
              />
              {recorder.isSupported && (
                <Button
                  variant={recorder.status === 'recording' ? 'destructive' : 'outline'}
                  size="icon"
                  className={cn('shrink-0', recorder.status === 'recording' && 'animate-pulse')}
                  onClick={handleMicClick}
                  disabled={isLoading || transcribing}
                  title={recorder.status === 'recording' ? 'Detener y transcribir' : 'Grabar nota de voz'}
                >
                  {transcribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : recorder.status === 'recording' ? (
                    <Square className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
              )}
              <Button
                size="icon"
                className="shrink-0 bg-blue-600 hover:bg-blue-700"
                onClick={sendMessage}
                disabled={isLoading || !input.trim()}
                title="Enviar"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
